import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ImageDataLike } from "gatsby-plugin-image";
import { Stack } from "../stack/Stack";
import { Typography } from "../typography/Typography";
import { useBackgroundColor } from "../../utils";
import { LinkType } from "../../types";
import { ThemeColorsKeys } from "../../styles/tokens/colors";
import { Image } from "../image/Image";
import { Link } from "../link/Link";
import Html from "../html/Html";

// Interface

export interface CardsProps {
  cards?: Card[];
  display: "2Up" | "3Up";
}

interface Card {
  heading: string;
  text?: string;
  link?: LinkType;
  backgroundColour: ThemeColorsKeys;
  image?: ImageDataLike | string;
  largeImage?: ImageDataLike | string;
  imagePlacement?: "left" | "top";
  narrow?: boolean; // Presentation only
}

export interface StyledColorBlockProps {
  backgroundColor: ThemeColorsKeys;
  headingOnly: boolean;
  narrow?: boolean;
  imagePlacement?: "left" | "top";
}

interface StyledCardsProps {
  columns: "2" | "3";
}

interface StyledCardProps {
  imagePlacement?: "left" | "top";
  hasLink: boolean;
  noImage: boolean;
}

// Styles

const StyledCards = styled.ul<StyledCardsProps>`
  ${({ theme: { space, media }, columns }) => css`
    display: grid;
    gap: ${space.xSmall};

    @media (min-width: ${media.large}) {
      grid-template-columns: repeat(${columns}, 1fr);
      gap: ${space.small};
    }
  `};
`;

const StyledCard = styled.li<StyledCardProps>`
  ${({
    theme: { colors, borderRadii, maxMedia },
    imagePlacement,
    hasLink,
    noImage,
  }) => css`
   
    ${useBackgroundColor("white")}
    display: ${imagePlacement === "left" ? "flex" : "block"};
    position: relative;
    border-radius: ${borderRadii.small};
    border: solid 1px ${colors.darkBlue};
    overflow: hidden;
    isolation: isolate;

    @media (max-width: ${maxMedia.large}) {
      ${imagePlacement === "left" &&
      (noImage
        ? css`
            display: block;
          `
        : css`
            display: flex;
          `)}

      ${imagePlacement === "top" &&
      css`
        display: block;
      `}
    }

    ${hasLink &&
    css`
      cursor: pointer;
      transition: all 0.2s ease-in-out;

      &:hover {
        box-shadow: 0 0 0 1px ${colors.darkBlue};
        opacity: 0.9;
      }
    `}

    a {
      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  `};
`;

const StyledColorBlock = styled.div<StyledColorBlockProps>`
  ${({
    theme: { space, colors, media },
    backgroundColor,
    headingOnly,
    narrow,
    imagePlacement,
    noImage,
  }) => css`
    ${useBackgroundColor(backgroundColor)}
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    ${imagePlacement === "left" &&
    css`
      border-right: solid 1px ${colors.darkBlue};
      flex-shrink: 0;
    `}

    ${imagePlacement === "top" &&
    css`
      border-bottom: solid 1px ${colors.darkBlue};
    `}

    ${headingOnly &&
    css`
      padding-top: ${space.fluidSmallToMedium};
      padding-bottom: ${space.fluidSmallToMedium};
      padding-left: ${space.fluidXSmallToMedium};
      padding-right: ${space.fluidXSmallToMedium};
      border-bottom: solid 1px ${colors.darkBlue};
      border-right: none;

      @media (min-width: ${media.large}) {
        max-width: 240px;
        min-width: 240px;
        border-right: solid 1px ${colors.darkBlue};
        border-bottom: none;

        ${imagePlacement === "top" &&
        css`
          max-width: none;
        `}
      }
    `}

    ${!headingOnly &&
    css`
      display: grid;
      place-items: center;
      padding: 0 ${space.fluidXSmallToLarge};

      ${imagePlacement !== "top" &&
      css`
        > * {
          max-width: ${narrow ? "48px" : "80px"};
        }
      `}

      ${imagePlacement === "top" &&
      css`
        padding: 0;
        max-width: none;
      `}

      @media (min-width: ${media.large}) {
        > * {
          max-width: ${narrow ? "64px" : "140px"};

          ${imagePlacement === "top" &&
          css`
            max-width: none;
          `}
        }
      }
    `}
  `};
`;

const StyledBody = styled(Stack)`
  ${({ theme: { space } }) => css`
    ${useBackgroundColor("white")}
    padding: ${space.fluidSmallToLarge};
    display: flex;
    flex-direction: column;

    a {
      word-break: break-all;
    }
  `};
`;

const StyledHeader = styled(Stack)`
  flex-grow: 1;
`;

const StyledColorBlockLink = styled(Link)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.xSmall};
  `};
`;

// JSX

const Card: FC<Card> = ({
  heading,
  text,
  link,
  backgroundColour,
  image,
  largeImage,
  narrow,
  imagePlacement,
}) => (
  <StyledCard imagePlacement={imagePlacement} hasLink={!!link} noImage={!image}>
    {image || heading ? (
      <StyledColorBlock
        backgroundColor={backgroundColour}
        headingOnly={!image}
        narrow={narrow}
        imagePlacement={imagePlacement}
      >
        {image && (
          <Image
            src={imagePlacement === "left" ? image : largeImage}
            alt={heading}
          />
        )}
        {!image && heading && (
          <Typography component="h3" variant="headingXSmall">
            {heading}
          </Typography>
        )}

        {link && !image && (
          <StyledColorBlockLink
            label=""
            a11yTitle={heading}
            url={link.url}
            target={link.target}
            dataLayerClickContext={`Cards - ${heading}`}
          />
        )}
      </StyledColorBlock>
    ) : (
      ""
    )}
    <StyledBody gap="xSmall">
      <StyledHeader gap="xSmall">
        {image && heading && (
          <Typography component="h3" variant="headingXSmall">
            {heading}
          </Typography>
        )}
        {text && <Html small>{text}</Html>}
      </StyledHeader>

      {link && image && (
        <Link {...link} dataLayerClickContext={`Cards - ${heading}`} />
      )}
    </StyledBody>
  </StyledCard>
);

export const Cards: FC<CardsProps> = ({ cards, display = "2Up" }) => (
  <StyledCards columns={display.replace("Up", "") as "2" | "3"}>
    {cards?.map((card, index) => (
      <Card {...card} narrow={display === "3Up"} key={index} />
    ))}
  </StyledCards>
);

export default Cards;
